import L from "leaflet";
import { useMap, useMapEvents } from "react-leaflet";
import { useEffect } from "react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const SA_CENTER = [-28.4793, 24.6727];
export const DEFAULT_ZOOM = 6;

// Map click handler component
export function MapClickHandler({ onMapClick, onRightClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
    contextmenu: (e) => {
      e.originalEvent.preventDefault();
      if (onRightClick) onRightClick(e.latlng.lat, e.latlng.lng, e);
    },
  });
  return null;
}

// Map center updater component
export function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, 12, { duration: 1.5 });
    }
  }, [center, map]);
  return null;
}

// Facility SVG icon factory
const FACILITY_ICONS = {
  hospital: `<svg viewBox="0 0 24 24" width="18" height="18"><rect x="2" y="2" width="20" height="20" rx="3" fill="#ef4444"/><path d="M12 6v12M6 12h12" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg>`,
  clinic: `<svg viewBox="0 0 24 24" width="18" height="18"><rect x="2" y="2" width="20" height="20" rx="3" fill="#10b981"/><path d="M12 7v4m0 0v4m0-4h4m-4 0H8" stroke="white" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="17" r="1" fill="white"/></svg>`,
  university: `<svg viewBox="0 0 24 24" width="18" height="18"><rect x="2" y="2" width="20" height="20" rx="3" fill="#3b82f6"/><path d="M12 5L4 10l8 5 8-5-8-5z" fill="white"/><path d="M6 12v4c0 2 6 3 6 3s6-1 6-3v-4" stroke="white" stroke-width="1.5" fill="none"/></svg>`,
  police: `<svg viewBox="0 0 24 24" width="18" height="18"><rect x="2" y="2" width="20" height="20" rx="3" fill="#8b5cf6"/><path d="M12 3l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4l2-4z" fill="white" transform="translate(0,3) scale(0.85)" transform-origin="12 12"/></svg>`,
};

export const createFacilityIcon = (type) => {
  return L.divIcon({
    className: '',
    html: FACILITY_ICONS[type] || FACILITY_ICONS.hospital,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  });
};

const COMPETITOR_ICON_SVG = `<svg viewBox="0 0 28 28" width="28" height="28"><circle cx="14" cy="14" r="13" fill="#f97316" stroke="white" stroke-width="2"/><path d="M9 11h10v7H9z" fill="none" stroke="white" stroke-width="1.5"/><path d="M7 11l3-4h8l3 4" stroke="white" stroke-width="1.5" fill="none"/><path d="M14 11v7" stroke="white" stroke-width="1"/></svg>`;

export const createCompetitorIcon = (size = 28) => {
  return L.divIcon({
    className: '',
    html: COMPETITOR_ICON_SVG,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2],
  });
};

const MALL_ICON_SVG = `<svg viewBox="0 0 28 28" width="28" height="28"><circle cx="14" cy="14" r="13" fill="#7c3aed" stroke="white" stroke-width="2"/><path d="M8 10h12l-1 10H9L8 10z" fill="none" stroke="white" stroke-width="1.5"/><path d="M10 10V8a4 4 0 018 0v2" stroke="white" stroke-width="1.5" fill="none"/></svg>`;

export const createMallIcon = () => {
  return L.divIcon({
    className: '',
    html: MALL_ICON_SVG,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
};

export const formatZAR = (amount) => {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const getHeatmapColor = (value) => {
  if (value < 0.33) return "#1e3a5f";
  if (value < 0.66) return "#3b82f6";
  return "#ef4444";
};

export const getOiHeatmapColor = (score) => {
  if (score >= 7.5) return "#059669";
  if (score >= 6.0) return "#10b981";
  if (score >= 4.5) return "#f59e0b";
  if (score >= 3.0) return "#f97316";
  return "#ef4444";
};

export const FACILITY_COLORS = {
  hospital: "#ef4444",
  clinic: "#10b981",
  university: "#3b82f6",
  police: "#8b5cf6",
};

export const getLsmColor = (band) => {
  const scale = [
    "#991b1b", "#dc2626", "#f97316", "#f59e0b", "#eab308",
    "#84cc16", "#22c55e", "#10b981", "#059669", "#047857",
  ];
  return scale[Math.max(0, Math.min(9, band - 1))];
};

export const TRADE_ZONE_COLORS = {
  primary: { color: "#059669", fillOpacity: 0.15 },
  secondary: { color: "#f59e0b", fillOpacity: 0.10 },
  tertiary: { color: "#ef4444", fillOpacity: 0.05 },
};
